/* Blackwater Soap Guild — "Roll for it" (d20). Homepage unification, V1.35.

   State machine: idle → rolling → result → rolling → …

   Rolling only REVEALS (owner decision, V1.35). Nothing is added to the cart
   until the shopper clicks the explicit "Add to cart · $9.00" button, which
   submits the hidden Dawn <product-form> (#candells-roll-form) exactly as every
   other add in the theme does — product-form.js listens for `submit` on that
   form and opens the cart drawer. Never replace it with a /cart/add.js fetch.

   Two independent random draws, by design (design_handoff_homepage/README.md §7):
   the 1–20 face is theatre, the pick is uniform over the pool. Coupling them
   would skew selection with catalogue size. Do not "fix" this.

   Markup contract (snippets/candells-roll-for-it.liquid):
     [data-candells-section="roll-for-it"]  section root, scopes everything
       [data-candells-roll-die]             gets .is-rolling for the tumble
       [data-candells-roll-face]            the numeral ("?" while rolling)
       [data-candells-roll-result]          role=status aria-live=polite card
       [data-candells-roll-result-image|-eyebrow|-name|-meta]
       [data-candells-roll-button]          primary; labels from data-label-*
       [data-candells-roll-add]             outline; label from data-label-template
       [data-candells-roll-variant-input]   the form's name="id" input
       [data-candells-roll-data]            application/json pool
   Elements carrying a [data-template] fill it by substituting [n] or [price]. */
(function () {
  'use strict';

  /* Must match the candells-roll-tumble keyframe duration in the stylesheet. */
  var TUMBLE_MS = 800;
  var FACE_COUNT = 20;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function setUp(section) {
    function find(selector) {
      return section.querySelector(selector);
    }

    var button = find('[data-candells-roll-button]');
    var addButton = find('[data-candells-roll-add]');
    var die = find('[data-candells-roll-die]');
    var face = find('[data-candells-roll-face]');
    var card = find('[data-candells-roll-result]');
    var cardImage = find('[data-candells-roll-result-image]');
    var cardEyebrow = find('[data-candells-roll-result-eyebrow]');
    var cardName = find('[data-candells-roll-result-name]');
    var cardMeta = find('[data-candells-roll-result-meta]');
    var variantInput = find('[data-candells-roll-variant-input]');
    var form = find('#candells-roll-form');
    var dataScript = find('[data-candells-roll-data]');

    if (!button || !addButton || !card || !variantInput || !form || !dataScript) return;

    var pool = [];
    try {
      pool = JSON.parse(dataScript.textContent || '[]');
    } catch (error) {
      pool = [];
    }
    pool = pool.filter(function (item) {
      return item && item.available && item.variantId;
    });

    /* Nothing sellable: say so on the button and wire nothing up. `disabled` is
       right here (unlike mid-tumble) because the state is permanent. */
    if (!pool.length) {
      button.disabled = true;
      if (button.dataset.labelEmpty) button.textContent = button.dataset.labelEmpty;
      return;
    }

    var state = 'idle';
    var pick = null;
    var timer = null;

    function fillTemplate(element, token, value) {
      if (!element) return;
      element.textContent = (element.dataset.template || '').replace('[' + token + ']', value);
    }

    /* Content first, [hidden] second: an aria-live region that is revealed before
       it is written announces unreliably (and sometimes announces nothing). */
    function reveal(faceValue) {
      state = 'result';
      if (die) die.classList.remove('is-rolling');
      if (face) face.textContent = faceValue;

      if (cardImage) {
        if (pick.image) {
          cardImage.src = pick.image;
          cardImage.hidden = false;
        } else {
          cardImage.removeAttribute('src');
          cardImage.hidden = true;
        }
      }
      /* alt stays empty: the card's own text names the scent, and this image sits
         inside the live region, so an alt would announce the title twice. */
      if (cardName) cardName.textContent = pick.title;
      fillTemplate(cardEyebrow, 'n', faceValue);
      fillTemplate(cardMeta, 'price', pick.price);
      card.hidden = false;

      addButton.textContent = (addButton.dataset.labelTemplate || '').replace('[price]', pick.price);
      addButton.hidden = false;

      if (button.dataset.labelAgain) button.textContent = button.dataset.labelAgain;
      button.removeAttribute('aria-disabled');

      /* The variant is staged on the form now so the Add click is a plain submit,
         but nothing is sent to /cart/add until that click happens. */
      variantInput.value = pick.variantId;

      document.dispatchEvent(
        new CustomEvent('candells:variant:picked', {
          detail: {
            variantId: pick.variantId,
            title: pick.title,
            url: pick.url,
            image: pick.image,
            size: '2oz',
            price: pick.price,
          },
        })
      );
    }

    function roll() {
      if (state === 'rolling') return; /* re-entrancy guard */

      /* Two independent draws — see the header comment. */
      var faceValue = 1 + Math.floor(Math.random() * FACE_COUNT);
      pick = pool[Math.floor(Math.random() * pool.length)];

      if (prefersReducedMotion()) {
        reveal(faceValue);
        return;
      }

      state = 'rolling';
      card.hidden = true;
      addButton.hidden = true;
      if (face) face.textContent = '?';
      if (button.dataset.labelRolling) button.textContent = button.dataset.labelRolling;
      /* aria-disabled, not disabled: a disabled button loses focus mid-tumble and
         a keyboard shopper is dropped back to the top of the document. */
      button.setAttribute('aria-disabled', 'true');

      if (die) {
        die.classList.remove('is-rolling');
        void die.offsetWidth; /* forces a reflow so the keyframes restart */
        die.classList.add('is-rolling');
      }

      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        reveal(faceValue);
      }, TUMBLE_MS);
    }

    button.addEventListener('click', roll);

    addButton.addEventListener('click', function () {
      if (state !== 'result' || !pick) return;
      variantInput.value = pick.variantId;
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true }));
      }
    });

    section._candellsDetach = function () {
      window.clearTimeout(timer);
      timer = null;
    };
  }

  function init(root) {
    if (!root || !root.querySelectorAll) return;
    var sections = root.querySelectorAll('[data-candells-section="roll-for-it"]');
    var i;
    for (i = 0; i < sections.length; i++) {
      if (sections[i].getAttribute('data-candells-roll-ready')) continue;
      sections[i].setAttribute('data-candells-roll-ready', 'true');
      setUp(sections[i]);
    }
  }

  function teardown(root) {
    if (!root || !root.querySelectorAll) return;
    var sections = root.querySelectorAll('[data-candells-section="roll-for-it"]');
    var i;
    for (i = 0; i < sections.length; i++) {
      if (typeof sections[i]._candellsDetach === 'function') {
        sections[i]._candellsDetach();
        sections[i]._candellsDetach = null;
      }
    }
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });

  /* The tag is deferred, so the document is already parsed. */
  init(document);
})();
